import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Handshake } from 'lucide-react';
import { DESTINATIONS } from '../data/destinations';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { PartnerCard } from '../components/PartnerCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_FILTERS = ['all', 'agency', 'dmc', 'operator', 'guide', 'accommodation', 'transport', 'experience'];
const COUNTRY_TO_DEST = { ST: 'sao-tome-principe', CV: 'cabo-verde' };

export default function PartnersPage() {
  const { t } = useLang();
  useSEO('Rede Local | Eco Collection', 'Agências, DMCs, tour operators, guias, alojamentos, transfers e experiências locais.');
  const [params, setParams] = useSearchParams();
  const [partners, setPartners] = useState([]);
  const type = params.get('type') || 'all';
  const [dest, setDest] = useState('all');

  useEffect(() => {
    fetch(`${API}/partners`)
      .then((r) => r.json())
      .then((d) => setPartners(Array.isArray(d) ? d : []))
      .catch(() => setPartners([]));
  }, []);

  const visible = useMemo(
    () =>
      partners.filter(
        (p) =>
          (type === 'all' || p.type === type) &&
          (dest === 'all' || COUNTRY_TO_DEST[p.country] === dest)
      ),
    [partners, type, dest]
  );

  const chip = (active) =>
    `rounded-full px-4 py-1.5 text-xs font-medium transition-colors duration-300 ${
      active ? 'bg-eco-green text-white' : 'bg-white text-eco-ink/60 hover:bg-eco-green/10 border border-eco-ink/10'
    }`;

  return (
    <div data-testid="partners-page">
      <PageHeader kicker={t('page.partners.k')} title={t('page.partners.t')} sub={t('page.partners.s')} />

      <section className="bg-eco-cream py-14 md:py-20">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-28 text-xs font-semibold tracking-wide uppercase text-eco-ink/40">{t('filter.destination')}</span>
              <button data-testid="partners-filter-dest-all" onClick={() => setDest('all')} className={chip(dest === 'all')}>{t('filter.all')}</button>
              {DESTINATIONS.map((d) => (
                <button key={d.slug} data-testid={`partners-filter-dest-${d.slug}`} onClick={() => setDest(d.slug)} className={chip(dest === d.slug)}>
                  {d.name}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-28 text-xs font-semibold tracking-wide uppercase text-eco-ink/40">{t('filter.type')}</span>
              {TYPE_FILTERS.map((f) => (
                <button key={f} data-testid={`partners-filter-type-${f}`} onClick={() => setParams(f === 'all' ? {} : { type: f })} className={chip(type === f)}>
                  {f === 'all' ? t('filter.all') : t(`type.${f}`)}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <PartnerCard
                  partner={p}
                  typeLabel={t(`type.${p.type}`)}
                  testid={`partners-card-${p.id}`}
                  cta={t('nav.explore')}
                />
              </Reveal>
            ))}
          </div>
          {visible.length === 0 && (
            <div data-testid="partners-empty" className="mt-10 rounded-3xl border border-dashed border-eco-ink/15 bg-white py-14 text-center">
              <p className="text-sm text-eco-ink/50">{t('partners.developing')}</p>
              <Link to="/plan-my-trip" data-testid="partners-empty-plan-cta" className="eco-btn-text mt-3 inline-flex justify-center">{t('nav.plan')} <ArrowRight size={14} /></Link>
            </div>
          )}

          <Reveal delay={0.1} className="mt-12">
            <div className="flex flex-col items-center gap-5 rounded-2xl bg-eco-deep px-7 py-8 text-center md:flex-row md:text-left">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-eco-gold/15 ring-1 ring-eco-gold/30">
                <Handshake size={24} className="text-eco-gold" />
              </span>
              <div className="flex-1">
                <h3 className="font-serif text-2xl font-semibold text-white">{t('join.t')}</h3>
                <p className="mt-1.5 text-sm text-white/60">{t('join.s')}</p>
              </div>
              <Link to="/join" data-testid="partners-join-cta" className="group inline-flex min-h-12 shrink-0 items-center gap-2 rounded-[10px] bg-eco-gold px-6 text-[15px] font-semibold text-eco-deep transition-colors duration-200 hover:bg-[#b8944f] md:min-h-11">
                {t('cta.become')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
